import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useSlaPolicies (SOUND-534 / backend SOUND-533) — the SLA policy catalog.
 * Internal-only global config CRUD at `/sla-policies`. Thin makeApiRequest
 * wrapper (house pattern; not React Query), auth + CompanyHeader from
 * useAuth()/useEffectiveCompanyId().
 *
 *   GET /sla-policies                 → { data: [policy] }   (active only)
 *   GET /sla-policies?includeInactive=true → { data: [policy] } (all)
 *
 * @returns {{policies: any[], loading: boolean, error: any, refetch: Function}}
 */
export function useSlaPolicies({ includeInactive = false, enabled = true } = {}) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchPolicies = useCallback(async () => {
    if (!enabled || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.listSlaPolicies(includeInactive ? { includeInactive: "true" } : {}),
        auth.user,
        companyId
      );
      setPolicies(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled, companyId, auth?.user, includeInactive]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  return { policies, loading, error, refetch: fetchPolicies };
}

/**
 * useSlaPolicy(policyId) — a single policy's detail. No-ops when policyId is
 * falsy or "new" (the create flow starts from a blank draft).
 * GET /sla-policies/:policyId → { data: policy }
 */
export function useSlaPolicy(policyId) {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(!!policyId && policyId !== "new");
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchPolicy = useCallback(async () => {
    if (!policyId || policyId === "new" || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getSlaPolicy(policyId),
        auth.user,
        companyId
      );
      setPolicy(res.data?.data || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [policyId, companyId, auth?.user]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  return { policy, loading, error, refetch: fetchPolicy };
}
